import { useEffect } from "react";

/**
 * Per-route head manager for social/share metadata.
 * Mirrors the JsonLd head-injection pattern (no extra dependency) so each
 * route can expose its own title, og:*, twitter:* and self-referencing url.
 * On unmount the per-route tags are removed so the static index.html
 * fallback applies again.
 */
interface SeoProps {
  title?: string;
  description?: string;
  /** Path or absolute url; relative paths are resolved against podgo.io. */
  url?: string;
  image?: string;
  type?: "website" | "article";
  /** When true, injects `<meta name="robots" content="noindex, nofollow">`. */
  noindex?: boolean;
}

const SITE_ORIGIN = "https://podgo.io";

function toAbsolute(value?: string): string | undefined {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE_ORIGIN}${value.startsWith("/") ? "" : "/"}${value}`;
}

function setMeta(attr: "name" | "property", key: string, content?: string) {
  if (!content) return () => {};
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  const created = !el;
  const previous = el?.getAttribute("content") ?? null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (!el) return;
    if (created) {
      el.remove();
    } else if (previous !== null) {
      el.setAttribute("content", previous);
    }
  };
}

function setCanonical(href?: string) {
  if (!href) return () => {};
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const created = !el;
  const previous = el?.getAttribute("href") ?? null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (!el) return;
    if (created) {
      el.remove();
    } else if (previous !== null) {
      el.setAttribute("href", previous);
    }
  };
}

export function Seo({ title, description, url, image, type = "website", noindex = false }: SeoProps) {
  useEffect(() => {
    const absUrl = toAbsolute(url);
    const absImage = toAbsolute(image);

    const previousTitle = document.title;
    if (title) document.title = title;

    const cleanups = [
      setMeta("name", "description", description),
      setMeta("property", "og:type", type),
      setMeta("property", "og:title", title),
      setMeta("name", "twitter:title", title),
      setMeta("property", "og:description", description),
      setMeta("name", "twitter:description", description),
      setMeta("property", "og:url", absUrl),
      setMeta("property", "og:image", absImage),
      setMeta("name", "twitter:image", absImage),
      setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow"),
      setCanonical(absUrl),
    ];

    return () => {
      document.title = previousTitle;
      cleanups.forEach((fn) => fn());
    };
  }, [title, description, url, image, type, noindex]);

  return null;
}
